import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { RigStyle } from '@/types/rig';

export interface VideoCablePreference {
  priority: string[];
  enabled: boolean;
}

export interface BatteryPlateRule {
  compatible_batteries: string[];
  description: string;
}

export interface BatteryPlateCompatibility {
  enabled: boolean;
  rules: Record<string, BatteryPlateRule>;
}

export interface AutoAddSettings {
  enabled_categories: string[];
  disabled_categories: string[];
}

export interface RigStylePreferenceFilter {
  name_contains?: string;
  exclude?: string;
  any_of?: string[];
  all_of?: string[];
}

export interface RigStylePreferenceRule {
  category: string;
  filter: RigStylePreferenceFilter | null;
  reason: string;
}

export interface RigStylePreferences {
  prefer: RigStylePreferenceRule[];
  avoid: RigStylePreferenceRule[];
}

export interface RigStyleRecommendations {
  enabled: boolean;
  singleItemCategories: string[];  // Categories where only ONE item is allowed per rig
  rules: Record<RigStyle, RigStylePreferences>;
  weightLimits: Record<RigStyle, number>;
  categoryPriorities: Record<RigStyle, string[]>;
}

export interface ChatbotSettings {
  enabled: boolean;
}

export interface RentalTrackingSettings {
  enabled: boolean;
  check_interval_hours: number;
  max_concurrent_checks: number;
  last_check_at: string | null;
}

export interface GlobalSettings {
  video_cable_preference: VideoCablePreference;
  battery_plate_compatibility: BatteryPlateCompatibility;
  auto_add_settings: AutoAddSettings;
  rig_style_recommendations: RigStyleRecommendations;
  chatbot_settings: ChatbotSettings;
  rental_tracking_settings: RentalTrackingSettings;
}

const defaultRigStylePreferences: RigStylePreferences = { prefer: [], avoid: [] };

const defaultSettings: GlobalSettings = {
  video_cable_preference: {
    priority: ['SDI 12G', 'SDI 6G', 'SDI 3G', 'SDI', 'HDMI'],
    enabled: true,
  },
  battery_plate_compatibility: {
    enabled: true,
    rules: {
      'mini-v-mount': {
        compatible_batteries: ['mini-v-mount'],
        description: 'Mini V-Mount plates only fit mini batteries',
      },
      'v-mount': {
        compatible_batteries: ['mini-v-mount', 'v-mount'],
        description: 'Full-size V-Mount plates fit both battery sizes',
      },
    },
  },
  auto_add_settings: {
    enabled_categories: ['cable'],
    disabled_categories: ['battery-plate', 'cage', 'handle', 'baseplate', 'rods', 'battery', 'monitor', 'wireless-video', 'matte-box', 'lens-support', 'cooling', 'mounting', 'evf', 'follow-focus', 'power-cable'],
  },
  rig_style_recommendations: {
    enabled: true,
    singleItemCategories: ['cage', 'baseplate', 'rods', 'battery-plate', 'matte-box'],
    rules: {
      'lightweight': defaultRigStylePreferences,
      'studio': defaultRigStylePreferences,
      'gimbal': defaultRigStylePreferences,
      'steadicam': defaultRigStylePreferences,
      'shoulder': defaultRigStylePreferences,
      'car-rig': defaultRigStylePreferences,
      'run-and-gun': defaultRigStylePreferences,
    },
    weightLimits: {
      'run-and-gun': 3000,
      'lightweight': 2000,
      'studio': 10000,
      'gimbal': 4500,
      'steadicam': 8000,
      'shoulder': 6000,
      'car-rig': 8000,
    },
    categoryPriorities: {
      'run-and-gun': ['cage', 'baseplate', 'rods', 'battery-plate', 'matte-box', 'monitor', 'handle', 'battery', 'wireless-video', 'evf', 'follow-focus', 'cooling', 'lens-support', 'mounting', 'cable'],
      'lightweight': ['cage', 'baseplate', 'rods', 'battery-plate', 'matte-box', 'handle', 'battery', 'monitor', 'wireless-video', 'evf', 'follow-focus', 'cooling', 'lens-support', 'mounting', 'cable'],
      'studio': ['cage', 'baseplate', 'rods', 'battery-plate', 'matte-box', 'mounting', 'monitor', 'follow-focus', 'evf', 'lens-support', 'wireless-video', 'handle', 'battery', 'cooling', 'cable'],
      'gimbal': ['cage', 'baseplate', 'rods', 'battery-plate', 'matte-box', 'handle', 'battery', 'monitor', 'wireless-video', 'evf', 'follow-focus', 'cooling', 'lens-support', 'mounting', 'cable'],
      'steadicam': ['cage', 'baseplate', 'rods', 'battery-plate', 'matte-box', 'handle', 'battery', 'monitor', 'wireless-video', 'evf', 'follow-focus', 'cooling', 'lens-support', 'mounting', 'cable'],
      'shoulder': ['cage', 'baseplate', 'rods', 'battery-plate', 'matte-box', 'handle', 'mounting', 'monitor', 'evf', 'follow-focus', 'lens-support', 'wireless-video', 'battery', 'cooling', 'cable'],
      'car-rig': ['cage', 'baseplate', 'rods', 'battery-plate', 'matte-box', 'mounting', 'monitor', 'wireless-video', 'battery', 'evf', 'follow-focus', 'lens-support', 'handle', 'cooling', 'cable'],
    },
  },
  chatbot_settings: {
    enabled: true,
  },
  rental_tracking_settings: {
    enabled: true,
    check_interval_hours: 24,
    max_concurrent_checks: 10,
    last_check_at: null,
  },
};

export const useGlobalSettings = () => {
  const [settings, setSettings] = useState<GlobalSettings>(defaultSettings);
  const [isLoading, setIsLoading] = useState(true);

  const fetchSettings = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('global_settings')
        .select('setting_key, setting_value, is_active')
        .eq('is_active', true);

      if (error) throw error;

      const parsed: GlobalSettings = { ...defaultSettings };
      
      data?.forEach((row) => {
        if (row.setting_key === 'video_cable_preference' && row.setting_value) {
          const value = row.setting_value as unknown as VideoCablePreference;
          if (value.priority && Array.isArray(value.priority)) {
            parsed.video_cable_preference = value;
          }
        }
        if (row.setting_key === 'battery_plate_compatibility' && row.setting_value) {
          const value = row.setting_value as unknown as BatteryPlateCompatibility;
          if (value.rules) {
            parsed.battery_plate_compatibility = value;
          }
        }
        if (row.setting_key === 'auto_add_settings' && row.setting_value) {
          const value = row.setting_value as unknown as AutoAddSettings;
          if (value.enabled_categories && value.disabled_categories) {
            parsed.auto_add_settings = value;
          }
        }
        if (row.setting_key === 'rig_style_recommendations' && row.setting_value) {
          const value = row.setting_value as unknown as RigStyleRecommendations;
          if (value.rules) {
            parsed.rig_style_recommendations = value;
          }
        }
        if (row.setting_key === 'chatbot_settings' && row.setting_value) {
          const value = row.setting_value as unknown as ChatbotSettings;
          if (typeof value.enabled === 'boolean') {
            parsed.chatbot_settings = value;
          }
        }
        if (row.setting_key === 'rental_tracking_settings' && row.setting_value) {
          const value = row.setting_value as unknown as RentalTrackingSettings;
          if (typeof value.enabled === 'boolean') {
            parsed.rental_tracking_settings = value;
          }
        }
      });

      setSettings(parsed);
    } catch (err) {
      console.error('Error fetching global settings:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const updateVideoCablePreference = async (preference: VideoCablePreference) => {
    try {
      const { error } = await supabase
        .from('global_settings')
        .update({ setting_value: preference as unknown as Record<string, never> })
        .eq('setting_key', 'video_cable_preference');

      if (error) throw error;
      
      setSettings(prev => ({
        ...prev,
        video_cable_preference: preference,
      }));
      
      return true;
    } catch (err) {
      console.error('Error updating video cable preference:', err);
      return false;
    }
  };

  const updateBatteryPlateCompatibility = async (compatibility: BatteryPlateCompatibility) => {
    try {
      const { error } = await supabase
        .from('global_settings')
        .update({ setting_value: compatibility as unknown as Record<string, never> })
        .eq('setting_key', 'battery_plate_compatibility');

      if (error) throw error;
      
      setSettings(prev => ({
        ...prev,
        battery_plate_compatibility: compatibility,
      }));
      
      return true;
    } catch (err) {
      console.error('Error updating battery plate compatibility:', err);
      return false;
    }
  };

  const updateAutoAddSettings = async (autoAddSettings: AutoAddSettings) => {
    try {
      const { error } = await supabase
        .from('global_settings')
        .update({ setting_value: autoAddSettings as unknown as Record<string, never> })
        .eq('setting_key', 'auto_add_settings');

      if (error) throw error;
      
      setSettings(prev => ({
        ...prev,
        auto_add_settings: autoAddSettings,
      }));
      
      return true;
    } catch (err) {
      console.error('Error updating auto-add settings:', err);
      return false;
    }
  };

  const updateRigStyleRecommendations = async (recommendations: RigStyleRecommendations) => {
    try {
      const { error } = await supabase
        .from('global_settings')
        .update({ setting_value: recommendations as unknown as Record<string, never> })
        .eq('setting_key', 'rig_style_recommendations');

      if (error) throw error;
      
      setSettings(prev => ({
        ...prev,
        rig_style_recommendations: recommendations,
      }));
      
      return true;
    } catch (err) {
      console.error('Error updating rig style recommendations:', err);
      return false;
    }
  };

  const updateChatbotSettings = async (chatbotSettings: ChatbotSettings) => {
    try {
      // Try update first, if no rows updated, insert
      const { data: existingData } = await supabase
        .from('global_settings')
        .select('id')
        .eq('setting_key', 'chatbot_settings')
        .single();

      if (existingData) {
        const { error } = await supabase
          .from('global_settings')
          .update({ setting_value: chatbotSettings as unknown as Record<string, never> })
          .eq('setting_key', 'chatbot_settings');

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('global_settings')
          .insert({
            setting_key: 'chatbot_settings',
            setting_value: chatbotSettings as unknown as Record<string, never>,
            description: 'AI Rig Assistant chatbot configuration',
            is_active: true,
          });

        if (error) throw error;
      }
      
      setSettings(prev => ({
        ...prev,
        chatbot_settings: chatbotSettings,
      }));
      
      return true;
    } catch (err) {
      console.error('Error updating chatbot settings:', err);
      return false;
    }
  };

  const updateRentalTrackingSettings = async (rentalSettings: RentalTrackingSettings) => {
    try {
      const { data: existingData } = await supabase
        .from('global_settings')
        .select('id')
        .eq('setting_key', 'rental_tracking_settings')
        .single();

      if (existingData) {
        const { error } = await supabase
          .from('global_settings')
          .update({ setting_value: rentalSettings as unknown as Record<string, never> })
          .eq('setting_key', 'rental_tracking_settings');

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('global_settings')
          .insert({
            setting_key: 'rental_tracking_settings',
            setting_value: rentalSettings as unknown as Record<string, never>,
            description: 'Rental tracking configuration',
            is_active: true,
          });

        if (error) throw error;
      }
      
      setSettings(prev => ({
        ...prev,
        rental_tracking_settings: rentalSettings,
      }));
      
      return true;
    } catch (err) {
      console.error('Error updating rental tracking settings:', err);
      return false;
    }
  };

  return {
    settings,
    isLoading,
    refetch: fetchSettings,
    updateVideoCablePreference,
    updateBatteryPlateCompatibility,
    updateAutoAddSettings,
    updateRigStyleRecommendations,
    updateChatbotSettings,
    updateRentalTrackingSettings,
  };
};
